import { useQuery } from "@tanstack/react-query";
import compact from "lodash/compact";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import { useFiltersV2Actions, useFiltersV2Select, useFiltersV2Store } from "PFApp/components/filters_v2";
import { getFilterOptionsQuery } from "PFApp/components/filters_v2/helpers/get_filter_options_query";
import { getFilterV2DisplayValue } from "PFApp/components/filters_v2/parts/filters_v2_preview/parts/filter_v2_preview_item/helpers/get_filter_v2_display_value";
import { hasChildrenFiltersSelector } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store_selectors";
import { FilterV2ComponentLabels } from "PFApp/components/filters_v2/types";
import { KeyValuePill } from "PFComponents/key_value_pill/key_value_pill";
import { isFilterValueOptionV2, isOptionsFilterV2 } from "PFCore/helpers/filters";
import { useDateFormatter } from "PFCore/hooks";
import { FilterV2 } from "PFTypes/filters_v2";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

type FilterV2PreviewItemProps = {
  filter: FilterV2;
  labels?: FilterV2ComponentLabels;
  disabled?: boolean;
};

const INFO_DELIMITER = ": ";
const getTitle = (infoText: string | null, text: string) =>
  [...(infoText ? [infoText] : []), text].join(INFO_DELIMITER);

export const FilterV2PreviewItem = ({ filter, disabled, labels }: FilterV2PreviewItemProps) => {
  const { formatDate } = useDateFormatter();
  const { t } = useTranslation("filters");
  const { t: tMisc } = useTranslation("translation");
  const { updateFilter } = useFiltersV2Actions();
  const hasChildrenFilters = useFiltersV2Store(hasChildrenFiltersSelector);

  const { searchId } = useFiltersV2Select("searchId");

  const query = getFilterOptionsQuery(filter, searchId);

  const selectedIds = isArray(filter.value)
    ? filter.value.map((value) => (isFilterValueOptionV2(value) ? value.id : value))
    : isString(filter.value) || isNumber(filter.value)
      ? [filter.value]
      : [];

  const { data: selectedValues, isFetching: isFetchingSelectedValues } = useQuery({
    queryKey: [filter.collection, filter.isChildren ? "children" : null, filter.name, filter.value],
    queryFn: () =>
      query!({ value: filter.value }).then((response) => {
        const entries = isArray(response) ? response : response.entries;
        const responseData = isArray(response) ? {} : response;
        return {
          ...responseData,
          entries: entries.map(({ id, text }) => ({ id, value: text, original: null }))
        };
      }),
    enabled: !!query && selectedIds.length === 1
  });

  const areAllOptionsSelected =
    isOptionsFilterV2(filter) &&
    Array.isArray(filter.value) &&
    filter.value.length === filter.options.length &&
    filter.value.length > 1;

  const isLogicalOr = filter.operator === "any";

  const getSelectedValuesLabel = useCallback(() => {
    if (isLogicalOr) {
      return t("preview.filtersSelectedOR", { count: selectedIds.length });
    }

    return t("preview.filtersSelected", { count: selectedIds.length });
  }, [isLogicalOr, selectedIds.length, t]);

  const text = useMemo(() => {
    if (isFetchingSelectedValues) {
      return tMisc("loading");
    }

    if (areAllOptionsSelected) {
      if (isLogicalOr) {
        return t("preview.anyFilterSelected");
      } else {
        return t("preview.allFilterSelected");
      }
    }

    if (selectedIds.length > 1) {
      return getSelectedValuesLabel();
    }

    if (!isEmpty(selectedValues?.entries)) {
      return selectedValues!.entries[0].value;
    }

    return getFilterV2DisplayValue({
      filter: { ...filter, statistics: (selectedValues as any)?.statistics },
      formatDate
    });
  }, [
    areAllOptionsSelected,
    filter,
    formatDate,
    getSelectedValuesLabel,
    isFetchingSelectedValues,
    isLogicalOr,
    selectedIds.length,
    selectedValues,
    t,
    tMisc
  ]);

  const infoText = useMemo(() => {
    if (!hasChildrenFilters || !labels) {
      return filter.title;
    }

    const sectionLabel = filter.isChildren ? labels.children : labels.main;
    return compact([sectionLabel, filter.title]).join(INFO_DELIMITER);
  }, [filter.title, filter.isChildren, hasChildrenFilters, labels]);

  const handleClear = useCallback(() => {
    updateFilter({
      ...filter,
      value: null
    });
  }, [filter, updateFilter]);

  if (isFetchingSelectedValues) {
    return null;
  }

  return (
    <KeyValuePill
      title={getTitle(infoText, text)}
      keyText={infoText ?? ""}
      value={isFetchingSelectedValues ? tMisc("loading") : text}
      onDelete={disabled ? undefined : handleClear}
      deleteTitle={t("clearAriaLabel")}
      qaId={`FiltersPreviewItem-${filter.name}`}
    />
  );
};
